import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { FileText, X } from "lucide-react";
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  useTheme,
} from "@mui/material";
import { Download, Visibility } from "@mui/icons-material";
import { fetchProductDocumentsByProductId } from "../../../redux/slice/product/createProductSlice";
import { fetchDocumentById } from "../../../redux/slice/practiceSlice";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import CustomTable from "../../common/CustomTable";
import { Column } from "../../../redux/slice/commonSlice";
import CustomIconButton from "../../common/CustomIconButton";
import { useModalStyles } from "../../common/useModalStyles";

interface ProductDocumentsModalProps {
  open: boolean;
  onClose: () => void;
  productId: number;
  productName: string;
  /** Pre-fetched docs — if provided, skips the internal fetch */
  initialDocs?: any[];
}

const ProductDocumentsModal: React.FC<ProductDocumentsModalProps> = ({
  open,
  onClose,
  productId,
  productName,
  initialDocs,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const dispatch = useAppDispatch();
  const { paperSx } = useModalStyles();
  const [productDocuments, setProductDocuments] = useState<any[]>(initialDocs ?? []);
  const [fetched, setFetched] = useState(!!initialDocs);
  const isDownloading = useRef(false);

  useEffect(() => {
    if (open && !fetched) {
      dispatch(fetchProductDocumentsByProductId(productId)).then((result) => {
        if (result.meta.requestStatus === "fulfilled") {
          const docs: any[] = (result.payload?.data ?? []).filter(
            (pd: any) => pd.entityName === "Document" && pd.docName !== null
          );
          setProductDocuments(docs);
        }
        setFetched(true);
      });
    }
  }, [open, fetched, dispatch, productId]);

  const handleClose = () => {
    setFetched(false);
    setProductDocuments([]);
    onClose();
  };

  const convertDocumentContentToFile = useCallback((documentData: any, entityId: string) => {
    const byteCharacters = atob(documentData.content);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], {
      type: documentData.fileType || "application/octet-stream",
    });

    const file = new File([blob], documentData.documentName || "Document", {
      type: documentData.fileType || "application/octet-stream",
    }) as File & { documentId?: string };

    file.documentId = entityId;
    return file;
  }, []);

  const fetchPreviewFile = useCallback(
    async (entityId: string) => {
      const result = await dispatch(fetchDocumentById(entityId));
      const documentData = result.payload;

      if (!documentData?.content) return undefined;

      return convertDocumentContentToFile(documentData, entityId);
    },
    [convertDocumentContentToFile, dispatch]
  );

  const handleDocumentPreview = useCallback(
    async (entityId: string) => {
      const previewFile = await fetchPreviewFile(entityId);
      if (previewFile) {
        const previewUrl = URL.createObjectURL(previewFile);
        window.open(previewUrl, "_blank", "noopener,noreferrer");
        window.setTimeout(() => URL.revokeObjectURL(previewUrl), 60_000);
      }
    },
    [fetchPreviewFile]
  );

  const handleDocumentDownload = useCallback(
    async (entityId: string, docName: string) => {
      if (isDownloading.current) return;
      isDownloading.current = true;
      try {
        const result = await dispatch(fetchDocumentById(entityId));
        const documentData = result.payload;
        if (documentData?.content) {
          const byteCharacters = atob(documentData.content);
          const byteArray = new Uint8Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++)
            byteArray[i] = byteCharacters.charCodeAt(i);
          const blob = new Blob([byteArray], {
            type: documentData.fileType || "application/octet-stream",
          });
          const blobUrl = URL.createObjectURL(blob);
          const anchor = document.createElement("a");
          anchor.href = blobUrl;
          anchor.download = documentData.documentName || docName;
          document.body.appendChild(anchor);
          anchor.click();
          document.body.removeChild(anchor);
          URL.revokeObjectURL(blobUrl);
        }
      } finally {
        isDownloading.current = false;
      }
    },
    [dispatch]
  );

  const docActionComponent = useCallback(
    (row: any): JSX.Element => (
      <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 0.5 }}>
        <CustomIconButton
          color="info"
          size="small"
          variant="outlined"
          tooltipMessage="Preview"
          tooltipPlacement="bottom"
          onClick={() => handleDocumentPreview(row.entityId)}
        >
          <Visibility fontSize="small" />
        </CustomIconButton>
        <CustomIconButton
          color="success"
          size="small"
          variant="outlined"
          tooltipMessage="Download"
          tooltipPlacement="bottom"
          onClick={() => handleDocumentDownload(row.entityId, row.docName)}
        >
          <Download fontSize="small" />
        </CustomIconButton>
      </Box>
    ),
    [handleDocumentPreview, handleDocumentDownload]
  );
//CS-1741 - Removed the status and inactive column
  const docColumns: Column[] = useMemo(
    () => [
      { id: "documentType", label: "Type" },
      { id: "docName", label: "Name" },
      { id: "effectiveDate", label: "Effective Date" },
      { id: "action", label: "Action"},
    ],
    []
  );

  const docRows = useMemo(
    () =>
      productDocuments.map((pd) => ({
        ...pd,
      })),
    [productDocuments]
  );

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
       PaperProps={{ sx: paperSx(false) }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pb: 1,
            borderBottom: `1px solid ${isDark ? "rgba(148,163,184,0.15)" : "rgba(0,0,0,0.08)"}`,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <FileText size={18} color={isDark ? "#2dd4bf" : "#0d9488"} />
            <Typography fontWeight={700} fontSize="1rem">
              Product Documents
            </Typography>
            <Typography
              fontSize="18px"
              sx={{ color: isDark ? "#94a3b8" : "#64748b" }}
            >
              — {productName}
            </Typography>
          </Box>
          <IconButton size="small" onClick={handleClose}>
            <X size={18} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          {fetched && docRows.length === 0 ? (
            <Typography
              sx={{
                textAlign: "center",
                py: 4,
                color: isDark ? "#64748b" : "#94a3b8",
                fontSize: "0.9rem",
              }}
            >
              No product documents found.
            </Typography>
          ) : (
            <Box
              sx={{
                "& .MuiTableCell-root": { fontSize: "0.78rem", py: 0.8, px: 1 },
                "& .MuiTableCell-head": { fontSize: "0.78rem", fontWeight: 700 },
              }}
            >
              <CustomTable
                columns={docColumns}
                rows={docRows}
                disablePagination={true}
                borderBox={true}
                showMenu={false}
                actionComponent={(row: any) => docActionComponent(row)}
                emptyText="No product documents found"
              />
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ProductDocumentsModal;
